import type { GlobalBetSlipState } from './GlobalBetSlipManager'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { initAnalytics } from '../lib/analytics'
import { setApiBase } from '../lib/api-client'
import App from './App'
import Dashboard from './Dashboard'
import { globalBetSlipManager } from './GlobalBetSlipManager'
import GlobalBetSlipOverlay from './GlobalBetSlipOverlay'
import styles from './styles.css?inline'

// Safari / older-mobile fallback CSS injected as a separate stylesheet
// AHEAD of the Tailwind bundle. Kept as a string literal so Tailwind's
// Vite plugin doesn't process it (otherwise the rules get merged into
// `@layer base` and become invisible on browsers without cascade-layer
// support — i.e. Safari < 15.4, which is the whole problem we're solving).
//
// Why each rule earns its place:
//
//   :host                       — Tailwind's `html, :host` reset is inside
//                                 @layer base, so on older Safari the host
//                                 element loses box-sizing, font, smoothing,
//                                 and the radius vars utilities depend on.
//
//   *, ::before, ::after        — Tailwind's universal reset (`border: 0
//                                 solid`) lives in @layer base too. Without
//                                 it, the UA stylesheet's `border-style:
//                                 none` wins and `border-2` shows no border.
//
//   button reset                — Safari paints its own bevel + rounded
//                                 corners on <button>. Strip with both
//                                 prefixed and unprefixed appearance.
//
//   input font-size: 16px       — iOS auto-zooms inputs with computed
//                                 font-size below 16px. The bet-slip stake
//                                 input would zoom the page on every tap.
//
//   --radius-* pinned values    — Tailwind v4 registers these via @property
//                                 in @layer theme; without @property
//                                 support the var() chain breaks, so
//                                 `rounded-md` falls back to nothing.
//
// We only inject this fallback into widget shadow roots, not the overlay's
// shadow root (which uses the same `styles` string), so both call sites get
// the same treatment via shadow.appendChild order.
const SAFARI_FALLBACK_STYLES = `
:host {
  box-sizing: border-box;
  display: block;

  /* Host pages sometimes attach a "loading" spinner animation to the embed
     container and clear it when content appears — but the widget renders
     into a shadow root, so the marker's light DOM stays empty forever and
     the spinner never stops, rotating everything inside the widget. For
     the shadow host, !important declarations from shadow styles win the
     cascade against the outer page, so this caps any such animation at a
     single iteration: an accidental infinite spinner stops after one turn,
     while legitimate finite entrance animations complete unchanged. */
  animation-iteration-count: 1 !important;
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
    'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  -webkit-text-size-adjust: 100%;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-tap-highlight-color: transparent;

  /* Radius tokens — Tailwind v4's @theme converts these to @property
     customs; without @property support the var() chain breaks and
     utilities like rounded-md resolve to nothing. Pinned values match
     Tailwind v4 defaults exactly. */
  --radius-sm: 0.25rem;
  --radius-md: 0.375rem;
  --radius-lg: 0.5rem;
  --radius-xl: 0.75rem;

  /* Initial values for every Tailwind v4 typed custom property the
     bundle registers via @property. On modern browsers @property gives
     these the same defaults; on older Safari without @property support
     these would resolve to the "guaranteed-invalid value" and break
     entire properties (transforms, shadows, borders, rings). Setting
     them on :host lets inheritance carry usable defaults down the tree
     without conflicting with utility overrides — when border-2 sets
     --tw-border-style: var(--tw-border-style) it picks up our value;
     when a utility sets --tw-border-style: dashed the new value wins
     by source order. */
  --tw-border-style: solid;
  --tw-translate-x: 0;
  --tw-translate-y: 0;
  --tw-translate-z: 0;
  --tw-scale-x: 1;
  --tw-scale-y: 1;
  --tw-scale-z: 1;
  --tw-rotate-x: 0;
  --tw-rotate-y: 0;
  --tw-rotate-z: 0;
  --tw-skew-x: 0;
  --tw-skew-y: 0;
  --tw-shadow: 0 0 #0000;
  --tw-shadow-color: transparent;
  --tw-shadow-alpha: 100%;
  --tw-inset-shadow: 0 0 #0000;
  --tw-inset-shadow-color: transparent;
  --tw-inset-shadow-alpha: 100%;
  --tw-ring-color: transparent;
  --tw-ring-shadow: 0 0 #0000;
  --tw-inset-ring-color: transparent;
  --tw-inset-ring-shadow: 0 0 #0000;
  --tw-ring-offset-width: 0px;
  --tw-ring-offset-color: #fff;
  --tw-ring-offset-shadow: 0 0 #0000;
  --tw-ring-inset: ;
  --tw-opacity: 1;
  --tw-blur: ;
  --tw-brightness: ;
  --tw-contrast: ;
  --tw-grayscale: ;
  --tw-hue-rotate: ;
  --tw-invert: ;
  --tw-saturate: ;
  --tw-sepia: ;
  --tw-drop-shadow: ;
  --tw-drop-shadow-color: transparent;
  --tw-drop-shadow-alpha: 100%;
  --tw-drop-shadow-size: ;
  --tw-backdrop-blur: ;
  --tw-backdrop-brightness: ;
  --tw-backdrop-contrast: ;
  --tw-backdrop-grayscale: ;
  --tw-backdrop-hue-rotate: ;
  --tw-backdrop-invert: ;
  --tw-backdrop-opacity: ;
  --tw-backdrop-saturate: ;
  --tw-backdrop-sepia: ;
  --tw-leading: ;
  --tw-font-weight: ;
  --tw-tracking: ;
  --tw-space-y-reverse: 0;
  --tw-divide-y-reverse: 0;
  --tw-duration: ;
}

/* box-sizing is the one safe universal selector to drop unlayered —
   Tailwind doesn't have a utility that sets box-sizing to anything
   else, so no override conflicts. */
*, *::before, *::after {
  box-sizing: border-box;
}

/* Form-element resets — Safari paints native bevels + system fonts
   that Tailwind's @layer base neutralizes. These selectors are
   element-only (no class), so any Tailwind utility class beats them
   by specificity inside @layer utilities, and we keep the floor for
   when those utilities aren't applied. */
button, input, select, textarea {
  font-family: inherit;
  line-height: inherit;
  color: inherit;
  margin: 0;
}
button, [role='button'] {
  cursor: pointer;
  background-color: transparent;
  background-image: none;
  padding: 0;
  -webkit-appearance: none;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
}
input, select, textarea {
  -webkit-appearance: none;
  appearance: none;
  /* iOS auto-zooms inputs whose computed font-size is < 16px. The
     bet-slip stake input would zoom the page on every tap without
     this floor. */
  font-size: 16px;
}

img, svg, video {
  display: block;
  max-width: 100%;
  height: auto;
}
`

// Dedicated QueryClient for the global bet slip overlay. The overlay lives
// in its own React tree (separate ReactDOM.createRoot on a body-level
// container) so it can't share App/Dashboard's per-instance providers.
const overlayQueryClient = new QueryClient()

// The dark theme's font stack leads with Poppins (NXTbets' face). @font-face
// registration doesn't cross shadow boundaries, so the font must be loaded
// on the host document itself. NXTbets pages already load Poppins; this is
// the fallback for dark-theme embeds on other sites. Same pattern as the
// approvals SDK.
const POPPINS_HREF = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap'
function ensurePoppinsLoaded(): void {
  if (typeof document === 'undefined')
    return
  if (document.querySelector(`link[href="${POPPINS_HREF}"]`))
    return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = POPPINS_HREF
  document.head.appendChild(link)
}

export interface WidgetConfig {
  league?: string
  mode?: 'widget' | 'dashboard'
  sports?: string[]
  updateHash?: boolean
  apiBase?: string
  /**
   * Visual theme. 'dark' applies the NXTbets dark palette (and consumes the
   * host page's --lp-* CSS variables when present).
   *
   * Resolution order when omitted: a `data-asd-theme="dark|light"` attribute
   * on the mount element or its ancestors wins; otherwise 'light', so
   * existing embeds are unaffected. Passing `theme` overrides both.
   */
  theme?: 'light' | 'dark'
  /**
   * How the widget renders when the sport has no odds. 'message' (default)
   * shows the static "no odds available" card; 'hidden' renders nothing.
   * Independently of this option, if an ancestor of the mount element
   * carries `data-asd-hide-when-empty`, that wrapper is hidden while the
   * sport is empty and restored if odds appear (standalone widget mode only).
   */
  emptyState?: 'message' | 'hidden'
  /**
   * Sub-affiliate embed flag. The bet-slip deep links open the
   * sub-affiliate's own book-issued URLs instead of ours; books they have
   * no link for get a no-op bet button. Server-side swap via ?aff=.
   */
  subAffiliate?: string
}

/**
 * Theme detection contract (first match wins):
 *   1. explicit `theme` init option
 *   2. `el.closest('[data-asd-theme]')` — NXTbets renders its embed markers
 *      as `<div data-asd-odds ... data-asd-theme="dark">`
 *   3. 'light'
 *
 * Nothing page-level is sniffed beyond this: NXTbets' dark theme is its
 * default and sets NO attribute on <html> (only light mode does), and
 * `prefers-color-scheme` is the visitor's OS setting, not the page's theme.
 * Resolved once at mount — pages don't change theme mid-session.
 */
function resolveTheme(config: WidgetConfig, containerElement: HTMLElement): 'light' | 'dark' {
  if (config.theme)
    return config.theme
  const declared = containerElement.closest('[data-asd-theme]')?.getAttribute('data-asd-theme')
  return declared === 'dark' ? 'dark' : 'light'
}

export interface WidgetInstance {
  destroy: () => void
  getBetSlip: () => GlobalBetSlipState
  clearBetSlip: () => void
}

/**
 * Container ID for the global overlay element
 * This div is appended to document.body and renders the shared bet slip overlay
 */
const OVERLAY_CONTAINER_ID = 'odds-widget-overlays'

/**
 * Odds Widget SDK Class
 *
 * Manages multiple widget instances and a single shared bet slip overlay.
 * Each widget renders in its own shadow DOM container, and the overlay also uses shadow DOM.
 * All widgets share the same global bet slip state.
 *
 * Architecture:
 * - Widget Instances: Each widget renders independently in its provided container using shadow DOM
 * - Global Overlay: Single bet slip overlay rendered to document.body using shadow DOM
 * - Shared State: All widgets share the same GlobalBetSlipManager instance
 * - Style Isolation: Both widgets and overlay use shadow DOM for complete CSS encapsulation
 *
 * Lifecycle:
 * 1. First widget init: Creates overlay container with shadow DOM and renders overlay
 * 2. Additional widgets: Just create new widget instances with their own shadow DOM
 * 3. Widget destruction: Cleans up individual instances and their bets
 * 4. Last widget destroyed: Removes overlay container from DOM
 */
class OddsWidgetSDK {
  private instances = new Map<string, WidgetInstance>()
  private overlayContainer: HTMLElement | null = null
  private overlayRoot: ReactDOM.Root | null = null
  private overlayMount: HTMLElement | null = null
  // Theme of the most recent init(). The bet slip overlay is shared across
  // all widget instances, so on a (pathological) mixed-theme page the
  // overlay follows the last-initialized widget.
  private theme: 'light' | 'dark' = 'light'

  init(container: string | HTMLElement, config: WidgetConfig): WidgetInstance {
    if (config.apiBase) {
      setApiBase(config.apiBase)
    }
    else {
      // Surface the misconfiguration loudly — a missing apiBase means the
      // widget uses its built-in default. Sportsbook logos, brand colors,
      // and bet-slip URLs all come from that origin, so this is almost
      // always a copy-paste mistake worth flagging to the embedder.
      console.warn('[OddsWidget] init() called without `apiBase`; using built-in default. Pass `apiBase` to point the widget at a specific host (e.g. https://grid.altsportsdata.com).')
    }

    initAnalytics()

    const containerElement = typeof container === 'string'
      ? document.querySelector(container) as HTMLElement
      : container

    if (!containerElement) {
      throw new Error('Container element not found')
    }

    this.theme = resolveTheme(config, containerElement)
    if (this.theme === 'dark')
      ensurePoppinsLoaded()

    // Ensure global overlay container exists, then apply the resolved theme
    // to its mount too — otherwise dark pages get a light bet slip overlay
    this.ensureOverlayContainer()
    this.overlayMount?.classList.toggle('dark', this.theme === 'dark')

    const shadowRoot = containerElement.attachShadow({ mode: 'open' })

    this.injectStylesIntoShadowDOM(shadowRoot)

    // React mounts on a wrapper div rather than the shadow root itself so
    // the theme class has an element to live on — `dark:` utilities and the
    // .dark token block in styles.css key off it.
    const mount = document.createElement('div')
    if (this.theme === 'dark')
      mount.classList.add('dark')
    shadowRoot.appendChild(mount)

    const instanceId = this.generateInstanceId()
    const root = ReactDOM.createRoot(mount)

    const instance: WidgetInstance = {
      destroy: () => this.destroyInstance(instanceId, root),
      getBetSlip: () => globalBetSlipManager.getBetsBySportsbook(),
      clearBetSlip: () => globalBetSlipManager.clearWidget(instanceId),
    }

    if (config.mode === 'dashboard') {
      root.render(React.createElement(Dashboard, {
        instanceId,
        sports: config.sports,
        updateHash: config.updateHash,
        emptyState: config.emptyState,
        subAffiliate: config.subAffiliate,
      }))
    }
    else {
      root.render(React.createElement(App, {
        instanceId,
        league: config.league ?? 'f1',
        emptyState: config.emptyState,
        hostElement: containerElement,
        subAffiliate: config.subAffiliate,
      }))
    }

    // Render global overlay (only once for all instances)
    this.renderGlobalOverlay()

    this.instances.set(instanceId, instance)
    return instance
  }

  /**
   * Ensure the global overlay container exists
   * Creates a full-screen div with shadow DOM for the bet slip overlay
   * The container itself has pointer-events: none, but child elements can have pointer events
   */
  private ensureOverlayContainer(): void {
    if (this.overlayContainer) {
      return
    }

    this.overlayContainer = document.getElementById(OVERLAY_CONTAINER_ID)

    if (!this.overlayContainer) {
      this.createNewOverlayContainer()
    }
    else if (!this.overlayRoot) {
      this.recreateOverlayShadowDOM()
    }
  }

  /**
   * Creates a brand new overlay container with shadow DOM setup
   */
  private createNewOverlayContainer(): void {
    this.overlayContainer = document.createElement('div')
    this.overlayContainer.id = OVERLAY_CONTAINER_ID
    this.overlayContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      z-index: 999999; /* High z-index to ensure overlay appears above all other content */
    `
    document.body.appendChild(this.overlayContainer)

    // Create shadow DOM for the overlay
    const shadowRoot = this.overlayContainer.attachShadow({ mode: 'open' })

    // Inject styles
    this.injectStylesIntoShadowDOM(shadowRoot)

    // Create React root on a themed mount div (see init for why)
    this.overlayMount = this.ensureMountElement(shadowRoot)
    this.overlayRoot = ReactDOM.createRoot(this.overlayMount)
  }

  /**
   * Recreates the shadow DOM setup for an existing overlay container
   * Used when container exists but React root was lost
   */
  private recreateOverlayShadowDOM(): void {
    if (!this.overlayContainer)
      return

    const shadowRoot = this.overlayContainer.shadowRoot
      || this.overlayContainer.attachShadow({ mode: 'open' })

    // Inject styles if not already present
    this.injectStylesIntoShadowDOM(shadowRoot)

    // Create React root on a themed mount div, reusing one left behind by
    // a previous SDK instance if the container survived
    this.overlayMount = this.ensureMountElement(shadowRoot)
    this.overlayRoot = ReactDOM.createRoot(this.overlayMount)
  }

  /**
   * Find-or-create the wrapper div React mounts on inside a shadow root.
   * The wrapper is what carries the theme class (`dark`), since a shadow
   * root has no element of its own to hang a class on.
   */
  private ensureMountElement(shadowRoot: ShadowRoot): HTMLElement {
    let mount = shadowRoot.querySelector<HTMLElement>('[data-ow-mount]')
    if (!mount) {
      mount = document.createElement('div')
      mount.setAttribute('data-ow-mount', '')
      shadowRoot.appendChild(mount)
    }
    return mount
  }

  /**
   * Strip `@layer <name> { ... }` wrappers from a CSS string, leaving the
   * inner rules as plain unlayered CSS. Tailwind v4 puts its theme,
   * reset, components, and utilities all inside @layer blocks; Safari
   * < 15.4 / iOS < 15.4 doesn't support @layer and silently drops the
   * entire block, so every color token, the universal reset, and many
   * utility variants get lost.
   *
   * Flattening preserves the source order of the rules — which on a
   * Tailwind v4 bundle is theme → base → components → utilities, the
   * same cascade you'd want even without layer semantics — and lets
   * older browsers render the widget correctly.
   *
   * The scanner is brace-aware (handles nested at-rules like @media,
   * @keyframes, @property inside @layer) and skips strings/comments
   * conservatively. `@layer name;` declaration-only forms are removed
   * since they're metadata-only.
   */
  private flattenCssLayers(css: string): string {
    let out = ''
    let i = 0
    while (i < css.length) {
      // Pass `/* ... */` comments through verbatim — never treat their
      // contents as code (avoids "false positive" @layer hits inside
      // comments).
      if (css[i] === '/' && css[i + 1] === '*') {
        const end = css.indexOf('*/', i + 2)
        if (end === -1) {
          out += css.slice(i)
          break
        }
        out += css.slice(i, end + 2)
        i = end + 2
        continue
      }
      // Match `@layer` not preceded by a letter (so we don't eat the
      // suffix of an identifier).
      if (css[i] === '@' && css.startsWith('@layer', i) && !/[a-z0-9_-]/i.test(css[i - 1] ?? '')) {
        let j = i + '@layer'.length
        // Read the names list until we hit `{` or `;`.
        while (j < css.length && css[j] !== '{' && css[j] !== ';') j++
        if (css[j] === ';') {
          // Declaration-only `@layer a, b;` — drop entirely.
          i = j + 1
          continue
        }
        if (css[j] === '{') {
          // Skip the `@layer name {`, then walk to the matching `}`.
          let depth = 1
          let k = j + 1
          const contentStart = k
          while (k < css.length && depth > 0) {
            const ch = css[k]
            if (ch === '{') depth++
            else if (ch === '}') depth--
            if (depth === 0) break
            k++
          }
          // Inner rules become plain top-level CSS. Recurse so any
          // nested @layer inside the block also flattens.
          out += this.flattenCssLayers(css.slice(contentStart, k))
          i = k + 1
          continue
        }
      }
      out += css[i]
      i++
    }
    return out
  }

  /**
   * Injects CSS styles into shadow DOM if not already present.
   *
   * Two separate <style> elements:
   *   1. Safari/older-mobile fallback — pinned values for Tailwind v4's
   *      typed `--tw-*` custom properties so the var() chain has a
   *      value when @property registration fails (Safari < 15.4).
   *      Also covers form-element resets and the iOS 16px input floor.
   *   2. Tailwind-processed bundle, with @layer wrappers flattened.
   *      Flattening lets older browsers parse the rules as plain CSS
   *      instead of silently dropping the entire @layer block.
   *
   * The flatten step trades a small bundle-time correctness assumption
   * for cross-browser reliability: Tailwind's cascade-layer ordering
   * (theme < base < components < utilities) collapses to plain source
   * order, which on a v4 build already emits in that same order.
   * Specificity within each tier behaves identically once flattened.
   */
  private injectStylesIntoShadowDOM(shadowRoot: ShadowRoot): void {
    if (!shadowRoot.querySelector('style')) {
      const fallbackEl = document.createElement('style')
      fallbackEl.textContent = SAFARI_FALLBACK_STYLES
      shadowRoot.appendChild(fallbackEl)

      // https://github.com/tailwindlabs/tailwindcss/issues/15005#issuecomment-2912976980
      let shadowRootStyles = styles.replace(/\(\(-webkit-hyphens:\s*none\)\)\s*and\s*/g, '')
      shadowRootStyles = this.flattenCssLayers(shadowRootStyles)
      const styleElement = document.createElement('style')
      styleElement.textContent = shadowRootStyles
      shadowRoot.appendChild(styleElement)
    }
  }

  /**
   * Render the global bet slip overlay
   * Only renders once when the first widget instance is created
   */
  private renderGlobalOverlay(): void {
    if (!this.overlayRoot) {
      return
    }

    // Render overlay when we have no instances yet (first time only)
    if (this.instances.size === 0) {
      this.overlayRoot.render(
        React.createElement(
          QueryClientProvider,
          { client: overlayQueryClient },
          React.createElement(GlobalBetSlipOverlay),
        ),
      )
    }
  }

  private destroyInstance(instanceId: string, root: ReactDOM.Root): void {
    root.unmount()

    // Clear bets from this widget
    globalBetSlipManager.clearWidget(instanceId)

    this.instances.delete(instanceId)

    // Clean up overlay if no instances remain
    if (this.instances.size === 0) {
      this.cleanupOverlayContainer()
    }
  }

  private cleanupOverlayContainer(): void {
    if (this.overlayRoot) {
      this.overlayRoot.unmount()
      this.overlayRoot = null
    }

    if (this.overlayContainer && this.overlayContainer.parentNode) {
      this.overlayContainer.parentNode.removeChild(this.overlayContainer)
      this.overlayContainer = null
    }
    this.overlayMount = null
  }

  private generateInstanceId(): string {
    return `widget-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }

  destroyAll(): void {
    // Create a copy to avoid modification during iteration
    const instancesCopy = Array.from(this.instances.values())
    instancesCopy.forEach(instance => instance.destroy())
    // Note: instances.clear() and cleanupOverlayContainer() are already handled
    // by destroyInstance() when the last instance is destroyed
  }
}

// Create and export SDK instance
const sdk = new OddsWidgetSDK()

// Global API for script tag usage
declare global {
  interface Window {
    OddsWidget: typeof sdk
  }
}

if (typeof window !== 'undefined') {
  window.OddsWidget = sdk
}
